import { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type { PolicyRule } from '../../types/policy.types';
import CategoryBadge from '../shared/CategoryBadge';
import RegulationCitation from '../shared/RegulationCitation';

interface RuleCardProps {
  rule: PolicyRule;
}

const SEVERITY_TOKENS: Record<PolicyRule['severity'], { ring: string; dot: string; label: string }> = {
  critical: { ring: 'ring-red-200', dot: 'bg-red-500', label: 'Critical' },
  high: { ring: 'ring-amber-200', dot: 'bg-amber-500', label: 'High' },
  medium: { ring: 'ring-yellow-200', dot: 'bg-yellow-400', label: 'Medium' },
  low: { ring: 'ring-gray-200', dot: 'bg-gray-400', label: 'Low' },
};

function formatParamValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return value.length === 1
      ? String(value[0])
      : `${value.length} item${value.length === 1 ? '' : 's'}`;
  }
  return String(value);
}

export default function RuleCard({ rule }: RuleCardProps) {
  const { t } = useTranslation('policy');
  const [expanded, setExpanded] = useState(false);
  const severity = SEVERITY_TOKENS[rule.severity];
  const hasOverride = rule.params.some((p) => p.is_overridden);

  return (
    <article className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow">
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-base font-semibold text-gray-900">{rule.name}</h3>
            <CategoryBadge category={rule.category} size="xs" />
            <span
              title={`${t('severity.label', { defaultValue: 'Severity' })}: ${severity.label}`}
              className={`inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-gray-600 ring-1 ring-inset ${severity.ring}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${severity.dot}`} aria-hidden="true" />
              {t(`severity.${rule.severity}`, { defaultValue: severity.label })}
            </span>
            {hasOverride && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                <ExclamationCircleIcon className="h-3 w-3" aria-hidden="true" />
                {t('rule.customized', { defaultValue: 'Customized' })}
              </span>
            )}
          </div>
          <p className="mt-1.5 text-sm text-gray-600">{rule.description_short}</p>
        </div>
      </header>

      {rule.regulation_refs.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {rule.regulation_refs.map((reg) => (
            <RegulationCitation key={`${reg.jurisdiction}-${reg.law}`} regulation={reg} />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
      >
        {expanded
          ? t('rule.collapse', { defaultValue: 'Hide details' })
          : t('rule.expand', { defaultValue: 'What this checks' })}
        {expanded ? (
          <ChevronUpIcon className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
          <p className="text-sm text-gray-700">{rule.description_long}</p>
          {rule.params.length > 0 && (
            <dl className="space-y-1.5 text-sm">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('rule.thresholds', { defaultValue: 'Thresholds' })}
              </dt>
              {rule.params.map((p) => (
                <div
                  key={p.name}
                  className={`flex flex-wrap items-baseline gap-2 rounded-md px-2 py-1 ${
                    p.is_overridden ? 'bg-amber-50' : 'bg-gray-50'
                  }`}
                >
                  <span className="text-gray-600">{p.label}:</span>
                  <span className="font-mono font-semibold text-gray-900">
                    {formatParamValue(p.current_value)}
                    {p.unit ? ` ${p.unit}` : ''}
                  </span>
                  {p.is_overridden && (
                    <span className="text-xs text-gray-500">
                      ({t('rule.defaultIs', { defaultValue: 'default' })}:{' '}
                      <span className="font-mono">{formatParamValue(p.default_value)}</span>
                      {p.unit ? ` ${p.unit}` : ''})
                    </span>
                  )}
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
    </article>
  );
}
